import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import BackHeader from '../components/BackHeader';
import NavigationBar from '../components/NavigationBar';
import useTranslation from '../hooks/useTranslation';
import { faqSections, FAQSection } from '../data/faq';
import { getCurrentLanguage } from '../config/languageConfig';
import { showChannelTalk, trackChannelTalkEvent } from '../services/ChannelService';

const FAQSectionPage: React.FC = () => {
  const { t } = useTranslation();
  const { sectionId } = useParams<{ sectionId: string }>();
  const navigate = useNavigate();
  const [currentLang, setCurrentLang] = useState(getCurrentLanguage());
  const [section, setSection] = useState<FAQSection | null>(null);

  // 언어 변경 감지
  useEffect(() => {
    const handleLanguageChange = () => {
      setCurrentLang(getCurrentLanguage());
    };

    window.addEventListener('languageChange', handleLanguageChange);
    return () => {
      window.removeEventListener('languageChange', handleLanguageChange);
    };
  }, []);

  // 섹션 데이터 찾기
  useEffect(() => {
    if (sectionId) {
      const foundSection = faqSections.find(s => s.id === sectionId);
      if (foundSection) {
        setSection(foundSection);
      } else {
        // 섹션을 찾을 수 없으면 FAQ 메인 페이지로 리다이렉트
        navigate('/faq');
      }
    }
  }, [sectionId, navigate]);

  // 채널톡 열기
  const handleGetHelp = async () => {
    try {
      await showChannelTalk();
      trackChannelTalkEvent('faq_section_get_help_clicked', {
        section_id: sectionId,
        section_title: section ? t(section.title) : '',
        source: 'faq_section_page'
      });
    } catch (error) {
      console.error('Failed to open ChannelTalk from FAQ section:', error);
    }
  };

  if (!section) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 헤더 */}
      <BackHeader title={t(section.title)} />
      
      {/* FAQ 아이템들 */}
      <div className="px-4 py-4 space-y-4">
        {section.items.map((item) => {
          const question = item.question[currentLang];
          const answer = item.answer[currentLang];

          return (
            <div key={item.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              {/* 질문 */}
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-800 leading-relaxed text-left">
                  Q. {question}
                </h3>
              </div>
              
              {/* 답변 */}
              <div className="text-gray-600 leading-relaxed text-left">
                <p className="whitespace-pre-line">
                  A: {answer}
                </p>
                {item.answerLink && (
                  <a
                    href={item.answerLink.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-3 inline-block text-sm font-medium text-red-600 underline underline-offset-2 hover:text-red-700"
                  >
                    {t(item.answerLink.label)}
                  </a>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Get Help 섹션 */}
      <div className="px-4 ">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center">
          <h3 className="text-lg font-semibold text-gray-800 mb-2">
            {t('faq.cantFindAnswer')}
          </h3>
          <p className="text-gray-600 mb-4">
            {t('faq.getHelpDescription')}
          </p>
          <button
            onClick={handleGetHelp}
            className="bg-red-500 hover:bg-red-600 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
          >
            {t('faq.getHelpNow')}
          </button>
        </div>
      </div>

      {/* 하단 여백 (NavigationBar 공간 확보) */}
      <div className="h-40"></div>

      {/* Navigation Bar */}
      <NavigationBar />
    </div>
  );
};

export default FAQSectionPage;
